import { useState } from 'react';
import type { GameView } from '@champdle/contracts';
import { avatarUrl } from '../api.js';
import { COLUMNS, describeTile, matchOf, valuesOf } from '../board.js';
import type { MatchKind } from '../board.js';
import type { ChampionItem } from './GuessInput.js';

interface Props {
  guesses: GameView['guesses'];
  byId: ReadonlyMap<string, ChampionItem>;
}

export function Icon({ match }: { match: MatchKind }) {
  const common = { width: 14, height: 14, viewBox: '0 0 14 14', 'aria-hidden': true } as const;
  if (match === 'correct')
    return (
      <svg {...common} className="tile__icon">
        <path d="M2.5 7.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.4" />
      </svg>
    );
  if (match === 'partial')
    return (
      <svg {...common} className="tile__icon">
        <path d="M2 7h10" fill="none" stroke="currentColor" strokeWidth="2.4" />
      </svg>
    );
  return (
    <svg {...common} className="tile__icon">
      <path d="M3 3l8 8M11 3l-8 8" fill="none" stroke="currentColor" strokeWidth="2.4" />
    </svg>
  );
}

/** Newest guess first. Only guesses added after the board mounted play the reveal. */
export function Board({ guesses, byId }: Props) {
  const [initial] = useState<ReadonlySet<number>>(() => new Set(guesses.map((g) => g.seq)));
  if (guesses.length === 0) return null;
  const rows = [...guesses].reverse();
  const newest = rows[0]!;
  const newestChamp = byId.get(newest.championId);

  return (
    <section className="board" aria-label="Your guesses">
      <div className="board__head" aria-hidden="true">
        <span>Champion</span>
        <span className="board__cols">
          {COLUMNS.map((c) => (
            <span key={c.key}>{c.label}</span>
          ))}
        </span>
      </div>
      <ol className="board__rows" aria-label="Your guesses, newest first" reversed>
        {rows.map((g) => {
          const champ = byId.get(g.championId);
          const name = champ?.name ?? g.championId;
          const fresh = !initial.has(g.seq);
          return (
            <li key={g.seq} className={fresh ? 'row is-new' : 'row'}>
              <div className="row__champ">
                {champ ? <img src={avatarUrl(champ.avatar)} alt="" width={48} height={48} /> : null}
                <span className="row__name">{name}</span>
                <span className="row__seq" aria-hidden="true">
                  #{g.seq}
                </span>
              </div>
              <ul className="tiles" aria-label={`${name}: attributes`}>
                {COLUMNS.map((c, i) => {
                  const match = matchOf(g.feedback, c.key);
                  const values = valuesOf(g.attributes, c.key);
                  const arrow = c.key === 'releaseYear' ? g.feedback.releaseYear.arrow : null;
                  return (
                    <li
                      key={c.key}
                      className={`tile tile--${match}`}
                      style={{ ['--i' as string]: i }}
                    >
                      <span className="sr-only">{describeTile(c.label, values, match, arrow)}</span>
                      <span className="tile__inner" aria-hidden="true">
                        <span className="tile__label">{c.label}</span>
                        <span className="tile__body">
                          <Icon match={match} />
                          <span className="tile__values">
                            {values.map((v) => (
                              <span key={v}>{v}</span>
                            ))}
                          </span>
                          {arrow ? (
                            <span className="tile__arrow">{arrow === 'up' ? '▲' : '▼'}</span>
                          ) : null}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
      <div role="status" className="sr-only">
        {fresh(initial, newest.seq)
          ? `${newestChamp?.name ?? newest.championId}: ${COLUMNS.map((c) =>
              describeTile(
                c.label,
                valuesOf(newest.attributes, c.key),
                matchOf(newest.feedback, c.key),
                c.key === 'releaseYear' ? newest.feedback.releaseYear.arrow : null,
              ),
            ).join('. ')}`
          : ''}
      </div>
    </section>
  );
}

function fresh(initial: ReadonlySet<number>, seq: number): boolean {
  return !initial.has(seq);
}
